import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCheckCircle,
  FiDollarSign,
  FiFileText,
  FiLoader,
  FiPrinter,
  FiRotateCcw,
  FiUser,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

export default function CreditNoteDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { accessToken } = useAuth();

  const [creditNote, setCreditNote] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadCreditNote = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest(`/sales-credit-notes/${id}`, {
        accessToken,
      });

      setCreditNote(response);
    } catch (requestError) {
      setError(requestError.message || "Could not load credit note.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    loadCreditNote();
  }, [loadCreditNote]);

  const salesReturn = creditNote?.salesReturn || creditNote?.return || null;

  const invoice =
    creditNote?.salesInvoice ||
    creditNote?.invoice ||
    salesReturn?.salesInvoice ||
    salesReturn?.invoice ||
    null;

  const returnItems = Array.isArray(salesReturn?.items)
    ? salesReturn.items
    : [];

  const totals = useMemo(() => {
    const creditAmount = Number(
      creditNote?.totalAmount ??
        creditNote?.creditAmount ??
        creditNote?.amount ??
        0,
    );

    const invoiceAmount = Number(invoice?.totalAmount || 0);
    const paidAmount = Number(invoice?.paidAmount || 0);
    const creditedAmount = Number(invoice?.creditedAmount || 0);

    return {
      creditAmount,
      invoiceAmount,
      paidAmount,
      creditedAmount,
      balanceAmount: Math.max(
        invoiceAmount - paidAmount - creditedAmount,
        0,
      ),
    };
  }, [creditNote, invoice]);

  if (isLoading) {
    return (
      <div className="min-h-[420px] flex items-center justify-center text-primary-700">
        <FiLoader className="animate-spin mr-3" size={24} />
        Loading credit note...
      </div>
    );
  }

  if (!creditNote) {
    return (
      <div className="bg-white border border-red-100 rounded-3xl p-8">
        <p className="text-red-700">{error || "Credit note not found."}</p>

        <button
          onClick={() => navigate("/admindashboard/sales/credit-notes")}
          className="mt-5 h-11 px-5 rounded-xl bg-primary-700 text-white"
        >
          Back to Credit Notes
        </button>
      </div>
    );
  }

  const status = toTitle(creditNote.status);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <button
            onClick={() => navigate("/admindashboard/sales/credit-notes")}
            className="flex items-center gap-2 text-gray-500 hover:text-primary-700 mb-4"
          >
            <FiArrowLeft />
            Back to Credit Notes
          </button>

          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-4xl font-bold text-gray-900">
              {creditNote.creditNoteCode}
            </h1>

            <CreditNoteStatusBadge status={status} />
          </div>

          <p className="text-gray-500 mt-3">
            Credit note generated from a received sales return.
          </p>
        </div>

        <button
          onClick={() => window.print()}
          className="h-11 px-5 rounded-xl border border-gray-200 bg-white flex items-center gap-2 hover:bg-gray-50"
        >
          <FiPrinter />
          Print
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700 flex gap-3">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {creditNote.status === "VOIDED" ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700">
          <p className="font-semibold">This credit note has been voided.</p>
          <p className="mt-1 text-sm">
            {creditNote.voidReason || "No void reason was recorded."}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-5 text-green-800 flex gap-3">
          <FiCheckCircle className="mt-0.5 shrink-0" size={20} />
          <div>
            <p className="font-semibold">Credit note is active.</p>
            <p className="mt-1 text-sm">
              The invoice receivable has been reduced by{" "}
              {formatMoney(totals.creditAmount)}.
            </p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          title="Credit Amount"
          value={formatMoney(totals.creditAmount)}
          icon={<FiDollarSign size={23} />}
          color="bg-primary-50 text-primary-700"
        />

        <StatCard
          title="Invoice Total"
          value={formatMoney(totals.invoiceAmount)}
          icon={<FiFileText size={23} />}
          color="bg-blue-50 text-blue-700"
        />

        <StatCard
          title="Invoice Paid"
          value={formatMoney(totals.paidAmount)}
          icon={<FiCheckCircle size={23} />}
          color="bg-green-50 text-green-700"
        />

        <StatCard
          title="Invoice Balance"
          value={formatMoney(totals.balanceAmount)}
          icon={<FiDollarSign size={23} />}
          color="bg-orange-50 text-orange-700"
        />
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <InfoPanel
          title="Customer Information"
          icon={<FiUser size={22} />}
          iconClassName="bg-primary-50 text-primary-700"
          rows={[
            [
              "Customer",
              invoice?.customerNameSnapshot ||
                creditNote.customer?.fullName ||
                "Walk-in Customer",
            ],
            ["Phone", invoice?.customerPhoneSnapshot],
            ["Email", invoice?.customerEmailSnapshot],
            ["Store", creditNote.store?.name || salesReturn?.store?.name],
          ]}
        />

        <InfoPanel
          title="Document Information"
          icon={<FiFileText size={22} />}
          iconClassName="bg-green-50 text-green-700"
          rows={[
            ["Credit Note Number", creditNote.creditNoteCode],
            [
              "Credit Note Date",
              formatDate(
                creditNote.issuedAt ||
                  creditNote.creditNoteDate ||
                  creditNote.createdAt,
              ),
            ],
            ["Sales Return", salesReturn?.returnCode],
            ["Sales Invoice", invoice?.invoiceCode],
            [
              "Issued By",
              creditNote.issuedBy?.employee?.fullName ||
                creditNote.issuedBy?.email,
            ],
          ]}
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="p-7 border-b border-gray-100 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold">Returned Items</h2>
            <p className="text-gray-500 mt-1">
              Item values used to generate this credit note.
            </p>
          </div>

          {salesReturn?.id && (
            <button
              onClick={() =>
                navigate(`/admindashboard/sales/returns/${salesReturn.id}`)
              }
              className="h-10 px-4 rounded-xl bg-primary-50 text-primary-700 font-medium flex items-center gap-2 hover:bg-primary-100"
            >
              <FiRotateCcw />
              View Sales Return
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[950px]">
            <thead className="bg-gray-50">
              <tr>
                <TableHeader>Product</TableHeader>
                <TableHeader>SKU</TableHeader>
                <TableHeader center>Returned Qty</TableHeader>
                <TableHeader center>Restocked</TableHeader>
                <TableHeader right>Credit Amount</TableHeader>
              </tr>
            </thead>

            <tbody>
              {returnItems.length ? (
                returnItems.map((item) => {
                  const invoiceItem =
                    item.salesInvoiceItem || item.invoiceItem || null;

                  const itemCreditAmount = Number(
                    item.creditAmount ??
                      item.lineCreditAmount ??
                      item.totalAmount ??
                      0,
                  );

                  return (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <td className="px-5 py-5">
                        <h3 className="font-semibold">
                          {item.productNameSnapshot ||
                            invoiceItem?.productNameSnapshot ||
                            "Product"}
                        </h3>

                        <p className="text-sm text-gray-500 mt-1">
                          {item.variantNameSnapshot ||
                            invoiceItem?.variantNameSnapshot ||
                            "Default variant"}
                        </p>
                      </td>

                      <td className="px-5 py-5 text-gray-600">
                        {item.skuSnapshot || invoiceItem?.skuSnapshot || "—"}
                      </td>

                      <td className="px-5 py-5 text-center font-semibold">
                        {Number(item.quantityReturned || 0)}
                      </td>

                      <td className="px-5 py-5 text-center">
                        {item.restockToInventory ? (
                          <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                            Yes
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                            No
                          </span>
                        )}
                      </td>

                      <td className="px-5 py-5 text-right font-semibold text-primary-700">
                        {formatMoney(itemCreditAmount)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="px-5 py-16 text-center text-gray-500">
                    No return item information was found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white rounded-3xl border border-gray-100 p-7">
          <h2 className="text-2xl font-bold">Notes</h2>

          <p className="mt-4 whitespace-pre-wrap text-gray-600">
            {creditNote.notes ||
              salesReturn?.notes ||
              "No notes were added to this credit note."}
          </p>
        </div>

        <div className="bg-primary-700 rounded-3xl p-7 text-white">
          <FiDollarSign size={28} />

          <p className="mt-6 text-sm text-white/70">Total Credit Issued</p>

          <p className="mt-2 text-3xl font-bold">
            {formatMoney(totals.creditAmount)}
          </p>

          <p className="mt-6 pt-5 border-t border-white/20 text-sm text-white/75">
            This credit reduces the invoice balance. It does not create a cash
            refund by itself.
          </p>
        </div>
      </div>
    </div>
  );
}

function InfoPanel({ title, icon, iconClassName, rows }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-7">
      <div className="flex items-center gap-3 mb-6">
        <div
          className={`h-11 w-11 rounded-2xl flex items-center justify-center ${iconClassName}`}
        >
          {icon}
        </div>

        <h2 className="text-xl font-bold">{title}</h2>
      </div>

      <div>
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex justify-between gap-5 py-4 border-b border-gray-100 last:border-b-0"
          >
            <span className="text-gray-500">{label}</span>
            <span className="font-semibold text-right">{value || "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold mt-3">{value}</p>
        </div>

        <div
          className={`h-14 w-14 rounded-2xl flex items-center justify-center ${color}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function TableHeader({ children, right = false, center = false }) {
  return (
    <th
      className={`px-5 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 ${
        right ? "text-right" : center ? "text-center" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function CreditNoteStatusBadge({ status }) {
  const colors = {
    Issued: "bg-green-100 text-green-700",
    Voided: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        colors[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {status}
    </span>
  );
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function toTitle(value = "") {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}